import { useEffect, useMemo, useState } from "react";
import { api, inr, currentMonth, monthLabel } from "../../lib/api";
import { MonthNav } from "../../components/MonthNav";
import { ReminderFilters } from "../../components/ReminderFilters";
import { ReminderRow } from "../../components/ReminderRow";
import { ReminderPreviewModal } from "../../components/ReminderPreviewModal";
import { ReminderTemplateEditor } from "../../components/ReminderTemplateEditor";
import { deriveRow, monthEndOverdue, fmtDay, matchesStatus, matchesSent } from "../../lib/reminders";

const Card = ({ label, value, sub, tag, accent = "text-white", testid }) => (
  <div className="bg-[#0A0A0A] p-5" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-2">
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      {tag && <span className="text-[9px] uppercase tracking-widest border border-white/15 text-white/50 px-1.5 py-0.5">{tag}</span>}
    </div>
    <p className={`font-heading text-3xl font-black ${accent}`}>{value}</p>
    {sub && <p className="text-white/40 text-[11px] mt-1.5">{sub}</p>}
  </div>
);

const SORTS = { due: (a, b) => (a.due || "9999").localeCompare(b.due || "9999"), overdue: (a, b) => b.days - a.days, amount: (a, b) => b.total - a.total };

export default function Reminders() {
  const [month, setMonth] = useState(currentMonth());
  const [board, setBoard] = useState(null);
  const [f, setF] = useState({ search: "", centre: "", batch: "", status: "all", category: "all", pkg: "all", sent: "all" });
  const [sort, setSort] = useState("due");
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/admin/reminders/board", { params: { month } }).then((r) => setBoard(r.data)).catch(() => setBoard({ rows: [] }));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const population = useMemo(() => (board?.rows || []).filter((r) => {
    const q = f.search.toLowerCase();
    if (q && !`${r.player.name} ${r.player.player_id}`.toLowerCase().includes(q)) return false;
    if (f.centre && r.player.centre !== f.centre) return false;
    if (f.batch && r.player.batch !== f.batch) return false;
    return f.pkg === "all" || r.package === f.pkg;
  }), [board, f.search, f.centre, f.batch, f.pkg]);

  const scoped = useMemo(() => population.map((r) => deriveRow(r, board.scope_end, f.category)).filter(Boolean), [population, board, f.category]);
  const list = useMemo(() => scoped.filter((r) => matchesStatus(r, f.status) && matchesSent(r.reminders_sent || 0, f.sent)).sort(SORTS[sort]), [scoped, f.status, f.sent, sort]);

  const overdueRows = scoped.filter((r) => r.status === "overdue");
  const overdueAmt = overdueRows.reduce((a, r) => a + r.overdueAmount, 0);
  const live = population.map((r) => deriveRow(r, board.w30, f.category)).filter(Boolean).map((r) => ({ dueToday: r.status === "due_today", due7: r.status === "due_7" }));
  const monthEndAmt = board?.is_past ? population.reduce((a, r) => a + monthEndOverdue(r, board.month_end, f.category), 0) : null;

  return (
    <div data-testid="admin-reminders">
      <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
        <MonthNav month={month} onChange={setMonth} testid="reminders-month-nav" />
        <p className="text-white/40 text-xs uppercase tracking-widest" data-testid="reminders-count">{list.length} player{list.length === 1 ? "" : "s"}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-3" data-testid="reminder-cards">
        <Card label="Overdue Players" value={overdueRows.length} accent="text-red-300" tag={`to ${monthLabel(month)}`} testid="card-overdue-players" />
        <Card label="Due Today" value={live.filter((x) => x.dueToday).length} accent="text-[#D4AF37]" tag="Live" sub={board ? fmtDay(board.today) : ""} testid="card-due-today" />
        <Card label="Due in Next 7 Days" value={live.filter((x) => x.due7).length} accent="text-blue-300" tag="Live" sub={board ? `to ${fmtDay(board.w7)}` : ""} testid="card-next-7" />
        <Card label="Total Overdue Amount" value={inr(overdueAmt)} accent="text-[#D4AF37]" tag="Outstanding today"
          sub={monthEndAmt !== null ? `At end of ${monthLabel(month)}: ${inr(monthEndAmt)} (before unapplied credit)` : null} testid="card-overdue-amount" />
      </div>
      <p className="text-white/30 text-[11px] mb-8" data-testid="reminder-scope-note">
        {board?.is_past
          ? `Showing amounts still outstanding today for invoices due up to ${monthLabel(month)}. Live cards use today's date.`
          : `Showing invoices due up to ${board ? fmtDay(board.scope_end) : "…"} (next 30 days), including older unpaid months. Amounts match the Dues tab. Packages are due 4 days after each package period begins; standard monthly and PT invoices on the 4th.`}
      </p>

      <ReminderFilters f={f} setF={setF} />

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="reminder-sort">
        <span className="text-[10px] uppercase tracking-widest text-white/40 mr-1">Sort</span>
        {[["due", "Due Date"], ["overdue", "Days Overdue"], ["amount", "Outstanding"]].map(([k, l]) => (
          <button key={k} onClick={() => setSort(k)} className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors duration-150 ${sort === k ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]"}`} data-testid={`reminder-sort-${k}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-2" data-testid="reminder-list">
        {!board && <p className="text-white/40 text-sm p-6">Loading…</p>}
        {board && list.map((r) => <ReminderRow key={r.player.player_id} r={r} onPreview={setPreview} onLogged={load} />)}
        {board && list.length === 0 && <p className="text-white/40 text-sm border border-white/10 p-8 text-center" data-testid="reminder-empty">Nothing to remind for these filters.</p>}
      </div>

      <ReminderTemplateEditor onSaved={load} />
      {preview && <ReminderPreviewModal row={preview} onClose={() => setPreview(null)} onLogged={() => { setPreview(null); load(); }} />}
    </div>
  );
}
